import { BN } from '@velocity-exchange/sdk';
import {
	ApyCalculationResult,
	ApyChunk,
} from 'src/hooks/useApyBreakdownTooltipData';
import { BigNum } from '@velocity-exchange/sdk';
import { BorrowMarketData } from 'src/hooks/useSyncBorrowLendBalances';
import { AprDetails } from 'src/hooks/earn/types';
import { getApyFromApr } from 'src/utils/borrowLend';
import { EarnProduct } from 'src/hooks/useEarnProduct';

// Utility function to safely create BigNum from potentially infinite values
// This can happen when we first init rewards and there are no deposits yet
// Caps at 1000% APR/APY if infinity
const safeBigNumFromPrint = (value: string | number, precision: BN): BigNum => {
	const numValue = typeof value === 'string' ? parseFloat(value) : value;
	if (!isFinite(numValue) || isNaN(numValue)) {
		return BigNum.fromPrint('1000', precision); // Cap at 1000%
	}
	// Also cap at 1000% for extremely high values
	const cappedValue = Math.min(numValue, 1000);
	return BigNum.fromPrint(cappedValue.toString(), precision);
};

export const getDepositData = (
	marketAprBreakdowns: AprDetails[],
	borrowLendData: BorrowMarketData | undefined,
	earnProductData: {
		baseSourceTitle: string;
		relevantBreakdowns: AprDetails[];
		baseApyType: 'if' | 'lend';
		baseApySource: string;
		baseApy: number;
	} | null,
	perpsCollateralRewardDetails: AprDetails | undefined,
	earnProduct: EarnProduct | null,
	isEarnProductBased: boolean,
	isMarketBased: boolean
): ApyCalculationResult => {
	let baseApy = 0;
	let precision = new BN(6);

	if (isMarketBased && borrowLendData) {
		const baseDepositApr = borrowLendData.depositApr
			.mul(BigNum.from(100))
			.toNum();
		baseApy = getApyFromApr(baseDepositApr, 'hourly');
		precision = borrowLendData.depositApr.precision;
	} else if (earnProductData) {
		baseApy = earnProductData.baseApy;
	}

	// Filter breakdowns based on usage pattern
	const additionalBreakdowns = isMarketBased
		? (marketAprBreakdowns?.filter((apr: AprDetails) => {
				return (
					apr.type !== 'native' &&
					apr.type !== 'borrow-reward' &&
					apr.type !== 'lend-oi-reward'
				);
			}) ?? [])
		: (earnProductData?.relevantBreakdowns ?? []);
	// For market-based, exclude native and borrow-reward from deposit calculations

	const nativeBreakdown = marketAprBreakdowns?.find(
		(apr: AprDetails) => apr.type === 'native'
	);

	// Calculate total APY
	const nativeApy = nativeBreakdown?.apy ?? 0;
	const additionalApy = additionalBreakdowns.reduce(
		(acc: number, curr: AprDetails) => {
			return acc + (curr.apy ?? 0);
		},
		0
	);

	const baseApyBN = safeBigNumFromPrint(baseApy, precision);
	// For BigNum calculations, use finite values only, but preserve original for display
	const safeAdditionalApy =
		isNaN(additionalApy) || !isFinite(additionalApy) ? 0 : additionalApy;
	const additionalApyBN = safeBigNumFromPrint(safeAdditionalApy, precision);
	const nativeApyBN = safeBigNumFromPrint(
		isNaN(nativeApy) ? 0 : nativeApy,
		precision
	);
	const perpsCollateralRewardApyBN = safeBigNumFromPrint(
		perpsCollateralRewardDetails?.apy ?? 0,
		precision
	);

	const totalApyBN = baseApyBN
		.add(additionalApyBN)
		.add(nativeApyBN)
		.add(perpsCollateralRewardApyBN);
	// Handle infinity cases and calculate final total APY
	const rawTotalApy = totalApyBN.toNum();
	let totalApy = rawTotalApy;

	// If any component is infinite or total exceeds 1000%, preserve the high value for display
	// but keep a finite value for calculations
	if (
		!isFinite(additionalApy) ||
		!isFinite(rawTotalApy) ||
		rawTotalApy > 1000
	) {
		totalApy = rawTotalApy > 1000 ? rawTotalApy : 1001; // Use actual value or 1001 to trigger "> 1000%" display
	}

	// Generate chunks
	const chunks: ApyChunk[] = [];

	// Main deposit chunk
	if (additionalBreakdowns.length > 0 || baseApy > 0 || nativeBreakdown) {
		const title = earnProductData?.baseSourceTitle ?? 'Lending APY';
		const sourceType = earnProductData?.baseApyType ?? 'lend';
		const sourceName = earnProductData?.baseApySource ?? 'Lending';

		chunks.push({
			title,
			apyBreakdown: [
				{
					type: sourceType,
					source: sourceName,
					apy: baseApy,
				},
				...additionalBreakdowns,
			],
		});
	}

	// Native yield chunk (if exists)
	if (nativeBreakdown) {
		chunks.push({
			title: nativeBreakdown.source,
			apyBreakdown: [nativeBreakdown],
		});
	}

	// Handle perps collateral reward
	if (
		perpsCollateralRewardDetails &&
		(!isEarnProductBased ||
			['all', 'borrow_lend'].includes(earnProduct?.productType ?? ''))
	) {
		chunks.push({
			title: 'Perps Collateral Reward',
			apyBreakdown: [
				{
					type: 'lend-oi-reward',
					source: 'Perps Collateral Reward',
					apy: perpsCollateralRewardDetails.apy,
					perpsCollateralReward:
						perpsCollateralRewardDetails.perpsCollateralReward,
				},
			],
			isPerpCollateralReward: true,
		});
	}

	const hasAtMostSingleBaseYield =
		chunks.length === 0 ||
		(chunks.length === 1 &&
			chunks[0].apyBreakdown.length === 1 &&
			(chunks[0].apyBreakdown[0].type === 'lend' ||
				chunks[0].apyBreakdown[0].type === 'if'));
	const showTooltip =
		!hasAtMostSingleBaseYield && earnProduct?.productType !== 'amplify';

	const showRewardsIcon =
		!!perpsCollateralRewardDetails ||
		chunks.some((chunk) =>
			chunk.apyBreakdown?.some((chunk) => chunk.type.includes('reward'))
		);

	return {
		baseApy,
		totalApy,
		totalApyBN,
		additionalBreakdowns,
		nativeBreakdown,
		showTooltip,
		chunks,
		showRewardsIcon,
	};
};
